import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getToastAutoDismissMs } from '../hooks/useToastPreferences';

type ToastType = 'success' | 'error' | 'info' | 'warning';
type ToastVariant = 'standard' | 'notification';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: number;
  title?: string;
  message: string;
  type: ToastType;
  variant: ToastVariant;
  icon?: string;
  actions: ToastAction[];
  durationMs: number;
}

interface ToastContextValue {
  addToast: (
    message: string,
    type?: ToastType,
    options?: {
      title?: string;
      variant?: ToastVariant;
      icon?: string;
      durationMs?: number;
      actions?: ToastAction[];
    },
  ) => void;
  dismissToast: (toastId: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 0;
const MAX_TOAST_COUNT = 20;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<number, number>>(new Map());
  const startedAtRef = useRef<Map<number, number>>(new Map());
  const remainingMsRef = useRef<Map<number, number>>(new Map());

  const clearToastTimer = useCallback((toastId: number) => {
    const timerId = timersRef.current.get(toastId);
    if (timerId !== undefined) {
      window.clearTimeout(timerId);
    }

    timersRef.current.delete(toastId);
    startedAtRef.current.delete(toastId);
  }, []);

  const scheduleToastDismiss = useCallback(
    (toastId: number, durationMs: number) => {
      if (durationMs <= 0) {
        return;
      }

      clearToastTimer(toastId);
      startedAtRef.current.set(toastId, Date.now());
      const timerId = window.setTimeout(() => {
        setToasts((prev) => prev.filter((toast) => toast.id !== toastId));
        clearToastTimer(toastId);
        remainingMsRef.current.delete(toastId);
      }, durationMs);
      timersRef.current.set(toastId, timerId);
    },
    [clearToastTimer],
  );

  const dismissToast = useCallback((toastId: number) => {
    clearToastTimer(toastId);
    remainingMsRef.current.delete(toastId);
    setToasts((prev) => prev.filter((toast) => toast.id !== toastId));
  }, [clearToastTimer]);

  const pauseToastDismiss = useCallback(
    (toastId: number) => {
      const remainingMs = remainingMsRef.current.get(toastId);
      const startedAt = startedAtRef.current.get(toastId);
      if (remainingMs === undefined || startedAt === undefined) {
        return;
      }

      const elapsedMs = Date.now() - startedAt;
      const updatedRemainingMs = Math.max(0, remainingMs - elapsedMs);
      remainingMsRef.current.set(toastId, updatedRemainingMs);
      clearToastTimer(toastId);
    },
    [clearToastTimer],
  );

  const resumeToastDismiss = useCallback(
    (toastId: number) => {
      const remainingMs = remainingMsRef.current.get(toastId);
      if (remainingMs === undefined) {
        return;
      }

      if (remainingMs <= 0) {
        dismissToast(toastId);
        return;
      }

      scheduleToastDismiss(toastId, remainingMs);
    },
    [dismissToast, scheduleToastDismiss],
  );

  const addToast = useCallback((message: string, type: ToastType = 'success', options?: {
    title?: string;
    variant?: ToastVariant;
    icon?: string;
    durationMs?: number;
    actions?: ToastAction[];
  }) => {
    const id = ++nextId;
    const durationMs = options?.durationMs ?? getToastAutoDismissMs();

    if (durationMs > 0) {
      remainingMsRef.current.set(id, durationMs);
      scheduleToastDismiss(id, durationMs);
    } else {
      remainingMsRef.current.delete(id);
    }

    setToasts((prev) => {
      const nextToasts = [
        ...prev,
        {
          id,
          title: options?.title,
          message,
          type,
          variant: options?.variant ?? 'standard',
          icon: options?.icon,
          actions: options?.actions ?? [],
          durationMs,
        },
      ];

      if (nextToasts.length <= MAX_TOAST_COUNT) {
        return nextToasts;
      }

      const overflowCount = nextToasts.length - MAX_TOAST_COUNT;
      const evictedToasts = nextToasts.slice(0, overflowCount);
      evictedToasts.forEach((toast) => {
        clearToastTimer(toast.id);
        remainingMsRef.current.delete(toast.id);
      });

      return nextToasts.slice(overflowCount);
    });
  }, [clearToastTimer, scheduleToastDismiss]);

  useEffect(() => {
    const timers = timersRef.current;
    const startedAtEntries = startedAtRef.current;
    const remainingEntries = remainingMsRef.current;

    return () => {
      timers.forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      timers.clear();
      startedAtEntries.clear();
      remainingEntries.clear();
    };
  }, []);

  const contextValue = useMemo(() => ({ addToast, dismissToast }), [addToast, dismissToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex max-h-[calc(100vh-2rem)] flex-col gap-2 overflow-y-auto pr-1">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            onMouseEnter={() => pauseToastDismiss(toast.id)}
            onMouseLeave={() => resumeToastDismiss(toast.id)}
            className={`max-w-md rounded-lg px-4 py-3 text-sm text-white shadow-lg transition-all ${
              toast.variant === 'notification' ? 'border border-white/20 bg-gray-900/95' : ''
            } ${
              toast.variant === 'standard'
                ? toast.type === 'success'
                  ? 'bg-green-600'
                  : toast.type === 'error'
                    ? 'bg-red-600'
                    : toast.type === 'warning'
                      ? 'bg-amber-600'
                      : 'bg-blue-600'
                : ''
            }`}
          >
            <div className="flex items-start gap-3">
              {toast.icon ? <span className="mt-0.5 text-base leading-none">{toast.icon}</span> : null}
              <div className="flex-1">
                {toast.title ? <p className="font-semibold">{toast.title}</p> : null}
                <p className={toast.title ? 'mt-1' : ''}>{toast.message}</p>
                {toast.actions.length > 0 ? (
                  <div className="mt-3 flex items-center gap-2">
                    {toast.actions.map((action) => (
                      <button
                        key={action.label}
                        type="button"
                        onClick={() => {
                          action.onClick();
                          dismissToast(toast.id);
                        }}
                        className="rounded-md border border-white/30 px-2.5 py-1 text-xs font-semibold text-white hover:bg-white/15"
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => dismissToast(toast.id)}
                className="text-white/80 hover:text-white"
                aria-label="Dismiss toast"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
}
